import { useState, useCallback } from 'react';
import * as api from '../../../api/api';

/**
 * Custom hook for managing upload functionality
 * Preserves all existing upload logic while organizing it better
 */
export const useUploadHandlers = (checkNetworkConnectivity, clearCacheAndRefresh, year, planOutcomeIds = []) => {
  // Spend Data Upload States
  const [spendUploadDialogOpen, setSpendUploadDialogOpen] = useState(false);
  const [spendUploadedFile, setSpendUploadedFile] = useState(null);
  const [spendUploadError, setSpendUploadError] = useState('');
  const [spendUploadSuccess, setSpendUploadSuccess] = useState('');
  const [spendUploading, setSpendUploading] = useState(false);
  const [spendUploadResult, setSpendUploadResult] = useState(null);
  const [spendUploadDept, setSpendUploadDept] = useState('');

  // Forecast Data Upload States
  const [forecastUploadDialogOpen, setForecastUploadDialogOpen] = useState(false);
  const [forecastUploadedFile, setForecastUploadedFile] = useState(null);
  const [forecastUploadError, setForecastUploadError] = useState('');
  const [forecastUploadSuccess, setForecastUploadSuccess] = useState('');
  const [forecastUploading, setForecastUploading] = useState(false);
  const [forecastUploadResult, setForecastUploadResult] = useState(null);
  const [forecastUploadDept, setForecastUploadDept] = useState('');

  // Advanced Options States
  const [advancedDialogOpen, setAdvancedDialogOpen] = useState(false);
  const [clearDataPassword, setClearDataPassword] = useState('');
  const [clearDataError, setClearDataError] = useState('');
  const [clearDataSuccess, setClearDataSuccess] = useState('');
  const [clearingData, setClearingData] = useState(false);
  const [clearDataResult, setClearDataResult] = useState(null);
  const [clearDataDept, setClearDataDept] = useState('');

  /**
   * Handle spend file upload - PRESERVES ALL EXISTING LOGIC
   */
  const handleSpendFileUpload = useCallback(async () => {
    if (!spendUploadedFile) {
      setSpendUploadError('Please select a file to upload');
      return;
    }

    setSpendUploading(true);
    setSpendUploadError('');
    setSpendUploadSuccess('');
    setSpendUploadResult(null);

    try {
      console.log('🚀 Starting spend upload:', {
        fileName: spendUploadedFile.name,
        fileSize: spendUploadedFile.size,
        fileType: spendUploadedFile.type
      });

      // Check network connectivity first
      const isConnected = await checkNetworkConnectivity();
      if (!isConnected) {
        throw new Error('Network connectivity issue detected. Please check your connection and try again.');
      }

      const formData = new FormData();
      formData.append('file', spendUploadedFile);

      const spendParams = new URLSearchParams();
      if (year) spendParams.set('year', year);
      if (spendUploadDept) spendParams.set('department', spendUploadDept);
      const apiUrl = `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000'}/api/outcomes/spend/upload${spendParams.toString() ? `?${spendParams}` : ''}`;
      console.log('📡 Spend upload URL:', apiUrl);

      const response = await fetch(apiUrl, {
        method: 'POST',
        body: formData,
        // Don't set Content-Type header - let browser set it with boundary for FormData
      });

      console.log('📡 Spend upload response status:', response.status);

      if (!response.ok) {
        let errorMessage = 'Upload failed';
        
        // Handle specific HTTP status codes
        if (response.status === 502) {
          errorMessage = 'Server temporarily unavailable (502 Bad Gateway). Please try again in a few moments.';
        } else if (response.status === 503) {
          errorMessage = 'Service temporarily unavailable (503). Please try again later.';
        } else if (response.status === 504) {
          errorMessage = 'Request timeout (504). The file may be too large or the server is busy.';
        } else {
          try {
            const errorData = await response.json();
            errorMessage = errorData.detail || errorData.message || errorMessage;
            console.error('❌ Spend upload error data:', errorData);
          } catch (parseError) {
            const errorText = await response.text();
            errorMessage = errorText || `HTTP ${response.status}: ${response.statusText}`;
            console.error('❌ Spend upload error text:', errorText);
          }
        }
        
        console.error('❌ Spend upload failed:', {
          status: response.status,
          statusText: response.statusText,
          url: apiUrl,
          errorMessage
        });
        
        throw new Error(errorMessage);
      }

            const result = await response.json();
      console.log('✅ Spend upload success:', result);
      
      // Set timestamp for intelligent retry logic
      if (typeof window !== 'undefined') {
        window.lastSpendUploadTime = Date.now();
      }
      
      setSpendUploadResult(result);

      const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);

      const parts = [];

      if (result.unmatched_total > 0) {
        parts.push(`${result.unmatched_projects?.length || 0} unmatched project codes totalling ${fmt(result.unmatched_total)} added to "Other"`);
      }

      if ((result.skipped_no_project_code || 0) > 0) {
        parts.push(`${result.skipped_no_project_code} row(s) with no project code routed to "Other"`);
      }

      if (planOutcomeIds.length > 0 && result.matched_project_details?.length > 0) {
        const planIdSet = new Set(planOutcomeIds);
        const outOfPlan = result.matched_project_details.filter(d => !planIdSet.has(d.outcome_id));
        if (outOfPlan.length > 0) {
          const outOfPlanTotal = outOfPlan.reduce((sum, d) => sum + (d.total || 0), 0);
          parts.push(`Note: ${fmt(outOfPlanTotal)} matched to ${outOfPlan.length} outcome(s) not in this plan — check Outcome Management to add them`);
        }
      }

      // Warn when the file contains data for multiple years — FVA only shows the selected year
      const byYear = result.summary?.by_year;
      if (byYear && Object.keys(byYear).length > 1) {
        const yearBreakdown = Object.entries(byYear)
          .sort(([a], [b]) => a - b)
          .map(([yr, d]) => `${yr}: ${fmt(d.total)}`)
          .join(', ');
        parts.push(`File spans multiple years — FVA shows only the selected year (${yearBreakdown})`);
      }

      const suffix = parts.length > 0 ? ` (${parts.join('; ')})` : '';
      setSpendUploadSuccess(`Successfully processed ${result.processed_records || 0} spend records${suffix}`);
      
      // Clear caches and refresh data
      console.log('🧹 Clearing caches after successful spend upload');
      
      if ('caches' in window) {
        caches.keys().then(names => {
          names.forEach(name => {
            if (name.includes('spend') || name.includes('forecast')) {
              caches.delete(name);
            }
          });
        }).catch(() => {}); // Ignore cache errors
      }
      
            // Give backend more time to process and save the data
      setTimeout(() => {
        console.log('🔄 Forcing fresh data fetch after spend upload...');
        clearCacheAndRefresh();
      }, 3000); // Increased from 1000ms to 3000ms

    } catch (error) {
      console.error('❌ Spend upload error:', error);
      setSpendUploadError(error.message || 'Failed to upload spend data');
    } finally {
      setSpendUploading(false);
    }
  }, [spendUploadedFile, spendUploadDept, year, checkNetworkConnectivity, clearCacheAndRefresh, planOutcomeIds]);

  /**
   * Handle forecast file upload - PRESERVES ALL EXISTING LOGIC
   */
  const handleForecastFileUpload = useCallback(async () => {
    if (!forecastUploadedFile) {
      setForecastUploadError('Please select a file to upload');
      return;
    }

    setForecastUploading(true);
    setForecastUploadError('');
    setForecastUploadSuccess('');
    setForecastUploadResult(null);

    try {
      console.log('🚀 Starting forecast upload:', {
        fileName: forecastUploadedFile.name,
        fileSize: forecastUploadedFile.size,
        fileType: forecastUploadedFile.type
      });

      // Check network connectivity first
      const isConnected = await checkNetworkConnectivity();
      if (!isConnected) {
        throw new Error('Network connectivity issue detected. Please check your connection and try again.');
      }

            const formData = new FormData();
      formData.append('file', forecastUploadedFile);

      const forecastParams = new URLSearchParams();
      if (year) forecastParams.set('year', year);
      if (forecastUploadDept) forecastParams.set('department', forecastUploadDept);
      const apiUrl = `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000'}/api/outcomes/forecast/upload${forecastParams.toString() ? `?${forecastParams}` : ''}`;
      console.log('📡 Forecast upload URL:', apiUrl);

      const response = await fetch(apiUrl, {
        method: 'POST',
        body: formData,
      });

      console.log('📡 Forecast upload response status:', response.status);

      if (!response.ok) {
        let errorMessage = 'Upload failed';
        
        // Handle specific HTTP status codes
        if (response.status === 502) {
          errorMessage = 'Server temporarily unavailable (502 Bad Gateway). Please try again in a few moments.';
        } else if (response.status === 503) {
          errorMessage = 'Service temporarily unavailable (503). Please try again later.';
        } else if (response.status === 504) {
          errorMessage = 'Request timeout (504). The file may be too large or the server is busy.';
        } else {
          try {
            const errorData = await response.json();
            errorMessage = errorData.detail || errorData.message || errorMessage;
            console.error('❌ Forecast upload error data:', errorData);
          } catch (parseError) {
            const errorText = await response.text();
            errorMessage = errorText || `HTTP ${response.status}: ${response.statusText}`;
            console.error('❌ Forecast upload error text:', errorText);
          }
        }
        
        console.error('❌ Forecast upload failed:', {
          status: response.status,
          statusText: response.statusText,
          url: apiUrl,
          errorMessage
        });
        
        throw new Error(errorMessage);
      }

            const result = await response.json();
      console.log('✅ Forecast upload success:', result);
      
      // Set timestamp for intelligent retry logic
      if (typeof window !== 'undefined') {
        window.lastForecastUploadTime = Date.now();
      }
      
      setForecastUploadResult(result);
      const forecastUnmatched = result.unmatched_total > 0
        ? ` (${result.unmatched_projects?.length || 0} unmatched project codes totalling ${new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(result.unmatched_total)} added to "Other")`
        : '';
      setForecastUploadSuccess(`Successfully processed ${result.processed_records || 0} forecast records${forecastUnmatched}`);
      
      // Clear caches and refresh data
      console.log('🧹 Clearing caches after successful forecast upload');
      
      if ('caches' in window) {
        caches.keys().then(names => {
          names.forEach(name => {
            if (name.includes('spend') || name.includes('forecast')) {
              caches.delete(name);
            }
          });
        }).catch(() => {}); // Ignore cache errors
      }
      
            // Give backend more time to process and save the data
      setTimeout(() => {
        console.log('🔄 Forcing fresh data fetch after forecast upload...');
        clearCacheAndRefresh();
      }, 3000); // Increased from 1000ms to 3000ms

    } catch (error) {
      console.error('❌ Forecast upload error:', error);
      setForecastUploadError(error.message || 'Failed to upload forecast data');
    } finally {
      setForecastUploading(false);
    }
  }, [forecastUploadedFile, forecastUploadDept, checkNetworkConnectivity, clearCacheAndRefresh, year]);

  /**
   * Handle clear uploaded data - PRESERVES ALL EXISTING LOGIC
   */
  const handleClearUploadedData = useCallback(async (clearType = 'all', clearDept = null) => {
    if (clearDataPassword !== 'clear123') {
      setClearDataError('Invalid password');
      return;
    }

    setClearingData(true);
    setClearDataError('');
    setClearDataSuccess('');
    setClearDataResult(null);

    try {
      // Use the proper API function
      const result = await api.clearUploadedData(clearDataPassword, clearType, clearDept);
      console.log('✅ Clear data API response:', result);
      
      setClearDataResult(result);
      setClearDataSuccess(`Successfully cleared ${result.cleared_files?.length || 0} data files`);
      
      // Aggressive cache clearing after data clear
      console.log('🧹 Performing aggressive cache clear after data deletion');
      
      // Clear browser caches
      if ('caches' in window) {
        caches.keys().then(names => {
          names.forEach(name => {
            caches.delete(name);
            console.log('🧹 Cleared browser cache:', name);
          });
        }).catch(() => {}); // Ignore cache errors
      }
      
      // Clear any localStorage related to forecasts
      if (typeof localStorage !== 'undefined') {
        Object.keys(localStorage).forEach(key => {
          if (key.includes('forecast') || key.includes('spend') || key.includes('outcome')) {
            localStorage.removeItem(key);
            console.log('🧹 Cleared localStorage:', key);
          }
        });
      }
      
      // Force a complete refresh with delay
      setTimeout(() => {
        console.log('🔄 Forcing complete data refresh after clear operation');
        clearCacheAndRefresh();
      }, 1500); // Longer delay to ensure backend has processed the clear

    } catch (error) {
      console.error('Clear data error:', error);
      setClearDataError(error.message || 'Failed to clear data');
    } finally {
      setClearingData(false);
    }
  }, [clearDataPassword, clearCacheAndRefresh]);

  /**
   * Close spend upload dialog
   */
  const handleCloseSpendUploadDialog = useCallback(() => {
    setSpendUploadDialogOpen(false);
    setSpendUploadedFile(null);
    setSpendUploadError('');
    setSpendUploadSuccess('');
    setSpendUploadResult(null);
    setSpendUploading(false);
    setSpendUploadDept('');
  }, []);

  /**
   * Close forecast upload dialog
   */
  const handleCloseForecastUploadDialog = useCallback(() => {
    setForecastUploadDialogOpen(false);
    setForecastUploadedFile(null);
    setForecastUploadError('');
    setForecastUploadSuccess('');
    setForecastUploadResult(null);
    setForecastUploading(false);
    setForecastUploadDept('');
  }, []);

  /**
   * Close advanced dialog
   */
  const handleCloseAdvancedDialog = useCallback(() => {
    setAdvancedDialogOpen(false);
    setClearDataPassword('');
    setClearDataError('');
    setClearDataSuccess('');
    setClearDataResult(null);
    setClearingData(false);
    setClearDataDept('');
  }, []);

  return {
    // Spend upload state and handlers
    spendUpload: {
      dialogOpen: spendUploadDialogOpen,
      setDialogOpen: setSpendUploadDialogOpen,
      uploadedFile: spendUploadedFile,
      setUploadedFile: setSpendUploadedFile,
      error: spendUploadError,
      setError: setSpendUploadError,
      success: spendUploadSuccess,
      setSuccess: setSpendUploadSuccess,
      uploading: spendUploading,
      result: spendUploadResult,
      setResult: setSpendUploadResult,
      dept: spendUploadDept,
      setDept: setSpendUploadDept,
      handleUpload: handleSpendFileUpload,
      handleClose: handleCloseSpendUploadDialog
    },

    // Forecast upload state and handlers
    forecastUpload: {
      dialogOpen: forecastUploadDialogOpen,
      setDialogOpen: setForecastUploadDialogOpen,
      uploadedFile: forecastUploadedFile,
      setUploadedFile: setForecastUploadedFile,
      error: forecastUploadError,
      setError: setForecastUploadError,
      success: forecastUploadSuccess,
      setSuccess: setForecastUploadSuccess,
      uploading: forecastUploading,
      result: forecastUploadResult,
      setResult: setForecastUploadResult,
      dept: forecastUploadDept,
      setDept: setForecastUploadDept,
      handleUpload: handleForecastFileUpload,
      handleClose: handleCloseForecastUploadDialog
    },
    
    // Advanced options state and handlers
    advanced: {
      dialogOpen: advancedDialogOpen,
      setDialogOpen: setAdvancedDialogOpen,
      password: clearDataPassword,
      setPassword: setClearDataPassword,
      error: clearDataError,
      setError: setClearDataError,
      success: clearDataSuccess,
      setSuccess: setClearDataSuccess,
      clearing: clearingData,
      result: clearDataResult,
      setResult: setClearDataResult,
      clearDept: clearDataDept,
      setClearDept: setClearDataDept,
      handleClear: handleClearUploadedData,
      handleClose: handleCloseAdvancedDialog
    }
  };
};